/**
 * Subscription Context
 * Provides a centralized SubscriptionClient with retained data support
 * Hooks can subscribe to multi-feed sub-channels without managing WebSocket connections directly
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { SubscriptionClient } from "../sdk";
import { ConnectionState, MultiFeedMessage } from "../sdk/types";
import { DEFAULT_WS_URL } from "../configs/base";

interface SubscriptionContextType {
  client: SubscriptionClient;
  isEnabled: boolean;
  isConnected: boolean;
  connectionState: ConnectionState;
  error: Error | null;
  toggleEnabled: () => void;
  setEnabled: (enabled: boolean) => void;
  clearError: () => void;
  // Retained data: latest data per sub-channel
  getRetainedData: (subChannel: string) => MultiFeedMessage | null;
  getAllRetainedData: () => Map<string, MultiFeedMessage>;
  clearRetainedData: (subChannel?: string) => void;
  // Subscription methods with retention
  subscribeMultiFeedWithRetention: (
    marketId: string,
    symbol: string,
    callback: (data: MultiFeedMessage) => void,
    interval?: string,
  ) => void;
  subscribeSubChannelWithRetention: (
    marketId: string,
    symbol: string,
    subChannel: string,
    callback: (data: any) => void,
    interval?: string,
  ) => void;
  unsubscribeMultiFeed: (
    marketId: string,
    symbol: string,
    callback?: (data: MultiFeedMessage) => void,
  ) => void;
  unsubscribeSubChannel: (
    subChannel: string,
    callback?: (data: any) => void,
  ) => void;
  // Configuration
  wsURL: string;
  setWsURL: (url: string) => void;
  authToken: string | undefined;
  setAuthToken: (token: string | undefined) => void;
  walletAddress: string | undefined;
  setWalletAddress: (address: string | undefined) => void;
  deviceID: string | undefined;
  setDeviceID: (deviceId: string | undefined) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
  defaultWsURL?: string;
  defaultAuthToken?: string;
  defaultWalletAddress?: string;
  defaultDeviceID?: string;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
  defaultWsURL = DEFAULT_WS_URL,
  defaultAuthToken,
  defaultWalletAddress,
  defaultDeviceID,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  );
  const [error, setError] = useState<Error | null>(null);
  const [wsURL, setWsURL] = useState(defaultWsURL);
  const [authToken, setAuthToken] = useState<string | undefined>(
    defaultAuthToken,
  );
  const [walletAddress, setWalletAddress] = useState<string | undefined>(
    defaultWalletAddress,
  );
  const [deviceID, setDeviceID] = useState<string | undefined>(
    defaultDeviceID,
  );

  // Initialize client immediately - it should never be null
  const clientRef = useRef<SubscriptionClient>(
    new SubscriptionClient({
      url: defaultWsURL,
      authToken: defaultAuthToken,
      walletAddress: defaultWalletAddress,
      deviceID: defaultDeviceID,
    })
  );
  const previousWsURLRef = useRef<string>(defaultWsURL);
  const unsubscribeStateChangeRef = useRef<(() => void) | null>(null);
  // Retained data: Map<subChannel, latest MultiFeedMessage>
  const retainedDataRef = useRef<Map<string, MultiFeedMessage>>(new Map());
  // Track wrapped callbacks for proper cleanup: Map<originalCallback, wrappedCallback>
  const wrappedCallbacksRef = useRef<Map<(data: any) => void, (data: any) => void>>(new Map());

  // Create/recreate subscription client when URL or credentials change
  useEffect(() => {
    // Check if we need to recreate the client (URL or credentials changed)
    const urlChanged = previousWsURLRef.current !== wsURL;
    const needsRecreation = urlChanged;

    if (needsRecreation) {
      // Clean up existing client
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      clientRef.current.disconnect();

      // Clear retained data when reconnecting to a new URL
      retainedDataRef.current.clear();

      // Create new client with current settings
      const client = new SubscriptionClient({
        url: wsURL,
        authToken,
        walletAddress,
        deviceID,
      });
      clientRef.current = client;
      previousWsURLRef.current = wsURL;
    }

    // Handle connection state based on isEnabled
    if (isEnabled) {
      // Track connection state if not already tracking
      if (!unsubscribeStateChangeRef.current) {
        const unsubscribeStateChange = clientRef.current.onStateChange((state) => {
          setConnectionState(state);
          setIsConnected(state === ConnectionState.Connected);
          // Clear error on successful connection
          if (state === ConnectionState.Connected) {
            setError(null);
          }
        });
        unsubscribeStateChangeRef.current = unsubscribeStateChange;
      }

      // Connect if not already connected
      if (!clientRef.current.isConnected()) {
        clientRef.current
          .connect()
          .then(() => {
            console.debug("[SubscriptionContext] Connected to WebSocket");
            setError(null);
          })
          .catch((err) => {
            const error =
              err instanceof Error ? err : new Error(String(err));
            console.error("[SubscriptionContext] Failed to connect:", error);
            setError(error);
          });
      }
    } else {
      // Disconnect when disabled, but keep client instance
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
      // Clear retained data when disabled
      retainedDataRef.current.clear();
      // Clear wrapped callbacks tracking
      wrappedCallbacksRef.current.clear();
      setConnectionState(ConnectionState.Disconnected);
      setIsConnected(false);
      setError(null);
    }

    return () => {
      // Cleanup on unmount or dependency change
      if (unsubscribeStateChangeRef.current) {
        unsubscribeStateChangeRef.current();
        unsubscribeStateChangeRef.current = null;
      }
      // Disconnect but keep client instance (never set to null)
      if (clientRef.current.isConnected()) {
        clientRef.current.disconnect();
      }
    };
  }, [isEnabled, wsURL, authToken, walletAddress, deviceID]);

  // Update client auth when credentials change
  useEffect(() => {
    if (clientRef.current && isEnabled) {
      if (authToken && walletAddress && deviceID) {
        clientRef.current.setAuthToken(authToken, walletAddress, deviceID);
      }
    }
  }, [isEnabled, authToken, walletAddress, deviceID]);

  /**
   * Subscribe to multi-feed channel with data retention
   * @param marketId - Market ID to subscribe to
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDC")
   * @param callback - Callback function for multi-feed updates
   * @param interval - Optional interval for kline data
   */
  const subscribeMultiFeedWithRetention = useCallback(
    (
      marketId: string,
      symbol: string,
      callback: (data: MultiFeedMessage) => void,
      interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn(
          "[SubscriptionContext] Cannot subscribe: client not initialized",
        );
        return;
      }

      // Validate inputs
      if (!marketId || typeof marketId !== "string") {
        console.warn(
          "[SubscriptionContext] Invalid marketId:",
          marketId,
        );
        return;
      }
      // Match MultiplexClient.validateSymbol: allow empty string or valid format with "/"
      if (symbol !== "" && (!symbol || typeof symbol !== "string" || !symbol.includes("/"))) {
        console.warn(
          "[SubscriptionContext] Invalid symbol (must be empty string or in format BASE/QUOTE):",
          symbol,
        );
        return;
      }

      // Wrap callback to retain data by sub-channel
      const wrappedCallback = (message: MultiFeedMessage) => {
        try {
          // Store retained data by sub-channel
          if (message && message.sub_channel) {
            retainedDataRef.current.set(message.sub_channel, message);
          }
          // Call original callback
          callback(message);
        } catch (err) {
          console.error(
            "[SubscriptionContext] Error in multi-feed callback:",
            err,
          );
        }
      };

      // Subscribe to main multi-feed channel
      try {
        // Build params object - only include symbol if provided and not empty, only include interval if provided
        const params: Record<string, any> = {};
        if (symbol && symbol !== "") {
          params.symbol = symbol;
        }
        if (interval) {
          params.interval = interval;
        }
        clientRef.current.subscribeMultiFeed(
          marketId,
          wrappedCallback,
          Object.keys(params).length > 0 ? params : undefined,
        );
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Subscription error:", error);
        setError(error);
      }
    },
    [],
  );

  /**
   * Subscribe to a specific sub-channel within multi-feed with data retention
   * @param marketId - Market ID to subscribe to
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDC")
   * @param subChannel - Sub-channel name (e.g., "orderbook", "trades", "positions")
   * @param callback - Callback function for sub-channel updates
   * @param interval - Optional interval for kline data
   */
  const subscribeSubChannelWithRetention = useCallback(
    (
      marketId: string,
      symbol: string,
      subChannel: string,
      callback: (data: any) => void,
      interval?: string,
    ) => {
      if (!clientRef.current) {
        console.warn(
          "[SubscriptionContext] Cannot subscribe: client not initialized",
        );
        return;
      }

      // Validate inputs
      if (!marketId || typeof marketId !== "string") {
        console.warn(
          "[SubscriptionContext] Invalid marketId:",
          marketId,
        );
        return;
      }
      // Match MultiplexClient.validateSymbol: allow empty string or valid format with "/"
      if (symbol !== "" && (!symbol || typeof symbol !== "string" || !symbol.includes("/"))) {
        console.warn(
          "[SubscriptionContext] Invalid symbol (must be empty string or in format BASE/QUOTE):",
          symbol,
        );
        return;
      }
      if (!subChannel || typeof subChannel !== "string") {
        console.warn(
          "[SubscriptionContext] Invalid subChannel:",
          subChannel,
        );
        return;
      }

      // Wrap callback to retain data
      // Handle both data-only payload (from MultiplexClient) and envelope format for safety
      const wrappedCallback = (payload: any) => {
        try {
          // Log orderbook data arrival
          if (subChannel === "orderbook") {
            console.log("[SubscriptionContext] Orderbook data received", {
              marketId,
              hasPayload: !!payload,
              payloadType: typeof payload,
              hasPayloadData: !!(payload as any)?.data,
            });
          }

          // Extract actual data - handle both envelope format { data: ... } and raw data
          const actualData = (payload as any)?.data ?? payload;
          
          // Log orderbook data extraction
          if (subChannel === "orderbook") {
            console.log("[SubscriptionContext] Orderbook data extracted", {
              marketId,
              hasData: !!actualData,
              dataType: typeof actualData,
              isObject: typeof actualData === "object",
              hasMarketId: !!(actualData as any)?.market_id,
              hasSequenceId: (actualData as any)?.sequence_id !== undefined,
              hasBids: !!(actualData as any)?.bids,
              hasAsks: !!(actualData as any)?.asks,
              bidsCount: Array.isArray((actualData as any)?.bids) ? (actualData as any).bids.length : 0,
              asksCount: Array.isArray((actualData as any)?.asks) ? (actualData as any).asks.length : 0,
            });
          }
          
          // Store retained data
          if (actualData && typeof actualData === "object") {
            // Create a proper MultiFeedMessage structure for retention
            const message: MultiFeedMessage = {
              sub_channel: subChannel,
              data: actualData,
              type: "data",
              channel: "multi_feed",
              market_id: marketId,
            };
            retainedDataRef.current.set(subChannel, message);
          }
          
          // Log before calling original callback
          if (subChannel === "orderbook") {
            console.log("[SubscriptionContext] Calling orderbook callback with data", {
              marketId,
              hasData: !!actualData,
            });
          }
          
          // Call original callback with the actual data
          callback(actualData);
          
          // Log after callback
          if (subChannel === "orderbook") {
            console.log("[SubscriptionContext] Orderbook callback completed", {
              marketId,
            });
          }
        } catch (err) {
          console.error(
            `[SubscriptionContext] Error in sub-channel callback for ${subChannel}:`,
            err,
          );
        }
      };

      // Track wrapped callback for proper cleanup
      wrappedCallbacksRef.current.set(callback, wrappedCallback);

      // Subscribe
      try {
        // Build params object - only include symbol if provided and not empty, only include interval if provided
        const params: Record<string, any> = {};
        if (symbol && symbol !== "") {
          params.symbol = symbol;
        }
        if (interval) {
          params.interval = interval;
        }
        clientRef.current.subscribeMultiFeedSubChannel(
          subChannel,
          marketId,
          wrappedCallback,
          Object.keys(params).length > 0 ? params : undefined,
        );

        // Send retained data after a short delay to ensure subscription is active
        // This fixes the race condition where retained data might be sent before subscription is ready
        const retained = retainedDataRef.current.get(subChannel);
        if (retained && retained.data) {
          // Use setTimeout to ensure subscription is processed first
          setTimeout(() => {
            try {
              callback(retained.data);
            } catch (err) {
              console.error(
                `[SubscriptionContext] Error sending retained data for ${subChannel}:`,
                err,
              );
            }
          }, 0);
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Subscription error:", error);
        setError(error);
      }
    },
    [],
  );

  /**
   * Unsubscribe from multi-feed channel
   * @param marketId - Market ID to unsubscribe from
   * @param symbol - Symbol to unsubscribe from
   * @param callback - Optional specific callback to remove
   */
  const unsubscribeMultiFeed = useCallback(
    (
      marketId: string,
      symbol: string,
      callback?: (data: MultiFeedMessage) => void,
    ) => {
      if (!clientRef.current) {
        return;
      }
      try {
        clientRef.current.unsubscribeMultiFeed(marketId, symbol, callback);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Unsubscribe error:", error);
        setError(error);
      }
    },
    [],
  );

  /**
   * Unsubscribe from a specific multi-feed sub-channel
   * @param subChannel - Sub-channel name to unsubscribe from
   * @param callback - Optional specific callback to remove (original callback, not wrapped)
   */
  const unsubscribeSubChannel = useCallback(
    (subChannel: string, callback?: (data: any) => void) => {
      if (!clientRef.current) {
        return;
      }
      try {
        // If callback provided, find the wrapped callback for proper cleanup
        if (callback) {
          const wrappedCallback = wrappedCallbacksRef.current.get(callback);
          if (!wrappedCallback) {
            console.warn(
              `[SubscriptionContext] Cannot unsubscribe: callback not found in wrapped callbacks for sub-channel ${subChannel}. ` +
              `Ensure you're using the same callback reference that was used for subscription.`
            );
            return;
          }
          clientRef.current.unsubscribeMultiFeedSubChannel(subChannel, wrappedCallback);
          wrappedCallbacksRef.current.delete(callback);
        } else {
          // No callback specified: unsubscribe all callbacks for this sub-channel
          clientRef.current.unsubscribeMultiFeedSubChannel(subChannel);
          // Note: We can't easily track which callbacks belong to which sub-channel,
          // so wrapped callbacks will be cleaned up on reconnect/disable
        }
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error(String(err));
        console.error("[SubscriptionContext] Unsubscribe error:", error);
        setError(error);
      }
    },
    [],
  );

  const toggleEnabled = useCallback(() => {
    setIsEnabled((prev) => !prev);
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  /**
   * Get retained data for a specific sub-channel
   * @param subChannel - Sub-channel name
   * @returns Retained MultiFeedMessage or null if not found
   */
  const getRetainedData = useCallback((subChannel: string) => {
    return retainedDataRef.current.get(subChannel) || null;
  }, []);

  /**
   * Get all retained data
   * @returns A copy of the Map containing all retained data
   */
  const getAllRetainedData = useCallback(() => {
    return new Map(retainedDataRef.current);
  }, []);

  /**
   * Clear retained data for a specific sub-channel or all if no subChannel provided
   * @param subChannel - Optional sub-channel name. If not provided, clears all retained data
   */
  const clearRetainedData = useCallback((subChannel?: string) => {
    if (subChannel) {
      retainedDataRef.current.delete(subChannel);
    } else {
      retainedDataRef.current.clear();
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Expose client with retained data wrappers
  const contextValue = useMemo(
    () => ({
      client: clientRef.current,
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      setWsURL,
      authToken,
      setAuthToken,
      walletAddress,
      setWalletAddress,
      deviceID,
      setDeviceID,
      // Expose subscription methods with retention
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    }),
    [
      isEnabled,
      isConnected,
      connectionState,
      error,
      toggleEnabled,
      setEnabled,
      clearError,
      getRetainedData,
      getAllRetainedData,
      clearRetainedData,
      wsURL,
      authToken,
      walletAddress,
      deviceID,
      subscribeMultiFeedWithRetention,
      subscribeSubChannelWithRetention,
      unsubscribeMultiFeed,
      unsubscribeSubChannel,
    ],
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 * Must be used within SubscriptionProvider
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within SubscriptionProvider",
    );
  }
  return context;
};
